import {
  applyDisplayNameFallbackToMany,
  getPublicProfilesByTournaments,
} from "@/lib/users";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useOrganizerTournaments } from "./useOrganizerTournaments";

/**
 * Admin hook for fetching all players (public profiles) who are participants
 * in any of the organizer's tournaments.
 *
 * This is useful for autocomplete fields when editing teams, as it provides
 * a pool of known players who have participated in the organizer's events.
 *
 * @param organizerId - The ID of the organizer
 * @returns TanStack Query result with unique players array
 */
export function useOrganizerPlayers(organizerId: string | undefined) {
  const t = useTranslations();
  const { data: tournaments = [], isLoading: isLoadingTournaments } =
    useOrganizerTournaments(organizerId);

  const tournamentIds = tournaments.map((t) => t.id);

  return useQuery({
    queryKey: ["organizer-players", organizerId, tournamentIds],
    queryFn: async () => {
      if (!organizerId || tournamentIds.length === 0) {
        return [];
      }

      const players = await getPublicProfilesByTournaments(tournamentIds);
      const playersWithFallback = applyDisplayNameFallbackToMany(
        players,
        t("common.player"),
      );

      // Sort players by name for easier selection
      return playersWithFallback.sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!organizerId && !isLoadingTournaments && tournamentIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes - players don't change frequently
  });
}
